import { useEffect, useRef, useState } from 'react';
import { useOnChange, usePageParam } from '../../hooks/usePageParam';
import { useQuery, useMutation } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ADMIN_COST_ANALYSIS_QUERY, ADMIN_COST_USERS_QUERY, ADMIN_USER_COST_DETAIL_QUERY } from '../../graphql/queries';
import { ADMIN_UPDATE_COST_RATES_MUTATION } from '../../graphql/mutations';
import Pagination from '../../components/common/Pagination';
import { CdnPlanOption, fmtCount, fmtGb, planShare, planStatus } from '../../utils/cdnPlan';
import CdnPlanTable from './CdnPlanTable';
import { Measurement, MeasurementPanel } from './CostMeasurement';
import CostOverview, { OverviewTotals, StorageOverview } from './CostOverview';
import BucketCheck from './BucketCheck';
import { FACTOR_RATE_KEY, StorageFactorKey } from '../../utils/storageFactors';
import DeliveryOverview from './DeliveryOverview';
import styles from './AdminCostAnalysis.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

type RateKey =
  | 'laneHeavyUsdPerHour' | 'laneStandardUsdPerHour' | 'laneLightUsdPerHour' | 'laneGpuUsdPerHour'
  | 's3UsdPerGbMonth' | 's3ArchiveUsdPerGbMonth'
  | 's3InfrequentUsdPerGbMonth' | 's3InstantArchiveUsdPerGbMonth' | 's3TieringUsdPer1000Objects'
  | 'storageFactorPointCloud' | 'storageFactorGaussian' | 'storageFactor2d' | 'storageFactor3d'
  | 'storageFactorOrthomosaic' | 'storageFactorVideo360' | 'intermediateKeepDays'
  | 'cloudfrontUsdPerGb' | 'cloudfrontUsdPer10kRequests' | 'egressUsdPerGb'
  | 'viewerGbPerView' | 'viewerRequestsPerView' | 'splatGbPerView' | 'splatRequestsPerView'
  | 'video360GbPerView' | 'video360RequestsPerView' | 'drawingGbPerView' | 'modelGbPerView' | 'orthoGbPerView'
  | 'drawingRequestsPerView' | 'modelRequestsPerView' | 'orthoRequestsPerView'
  | 'fixedMonthlyUsd' | 'machineHeavyUsdPerHour' | 'machineStandardUsdPerHour' | 'machineLightUsdPerHour'
  | 'machineGpuUsdPerHour' | 'mapTilesEsriUsdPer1000' | 'mapTilesEsriFreePerMonth'
  | 'mapTilesGoogleUsdPer1000' | 'mapTilesGoogleFreePerMonth';

interface Bucket {
  key: string; label: string; jobs: number; runHours: number; machineHours: number;
  computeUsd: number; gbProcessed: number; avgPerJobUsd: number; avgPerGbUsd: number;
}
interface UserCost {
  userId: string; name: string; email: string; company: string; subusers: number;
  jobs: number; failedJobs: number; runHours: number; computeUsd: number; gbProcessed: number;
  storageGb: number; storageUsd: number; downloads: number; downloadGb: number;
  shareDownloads: number; views: number; transferUsd: number;
  totalUsd: number; avgPerConversionUsd: number; avgPerGbUsd: number;
}

const USERS_PAGE = 25;

const FACTOR_HINT = 'calculator only: GB stored per GB uploaded, original + converted. Check the bucket shows the measured value';
const CALC_VIEW_HINT = 'calculator only: one open of this type';

const RATE_FIELDS: { key: RateKey; label: string; hint: string; step: number }[] = [
  { key: 'machineHeavyUsdPerHour', label: 'Heavy machine $/hour', hint: 'r5.2xlarge + 500 GB EBS: jobs with measured machine time', step: 0.01 },
  { key: 'machineStandardUsdPerHour', label: 'Standard machine $/hour', hint: 'r5.2xlarge + 500 GB EBS: jobs with measured machine time', step: 0.01 },
  { key: 'machineLightUsdPerHour', label: 'Light machine $/hour', hint: 'c5.xlarge + 30 GB EBS: jobs with measured machine time', step: 0.01 },
  { key: 'machineGpuUsdPerHour', label: 'GPU machine $/hour', hint: 'g5.xlarge + 500 GB EBS: jobs with measured machine time', step: 0.01 },
  { key: 'laneHeavyUsdPerHour', label: 'Heavy lane $/job-hour', hint: 'jobs without measured machine time; one job per machine', step: 0.01 },
  { key: 'laneStandardUsdPerHour', label: 'Standard lane $/job-hour', hint: 'jobs without measured machine time; two jobs per machine', step: 0.01 },
  { key: 'laneLightUsdPerHour', label: 'Light lane $/job-hour', hint: 'jobs without measured machine time; two jobs per machine', step: 0.01 },
  { key: 'laneGpuUsdPerHour', label: 'GPU lane $/job-hour', hint: 'jobs without measured machine time; splat training', step: 0.01 },
  { key: 's3UsdPerGbMonth', label: 'S3 $/GB-month', hint: 'S3 Standard, and Intelligent-Tiering\'s Frequent Access tier (same price)', step: 0.001 },
  { key: 's3InfrequentUsdPerGbMonth', label: 'S3 Infrequent $/GB-month', hint: 'Intelligent-Tiering after 30 days without a read', step: 0.0001 },
  { key: 's3InstantArchiveUsdPerGbMonth', label: 'S3 Archive Instant $/GB-month', hint: 'Intelligent-Tiering after 90 days without a read', step: 0.0001 },
  { key: 's3TieringUsdPer1000Objects', label: 'Intelligent-Tiering $/1,000 objects', hint: 'monitoring fee a month, objects over 128 KB', step: 0.0001 },
  { key: 's3ArchiveUsdPerGbMonth', label: 'S3 archive $/GB-month', hint: 'Glacier Flexible Retrieval: where the archive lifecycle rules move old files', step: 0.0001 },
  { key: 'storageFactorPointCloud', label: 'Storage factor: point cloud', hint: FACTOR_HINT, step: 0.05 },
  { key: 'storageFactorGaussian', label: 'Storage factor: Gaussian splat', hint: FACTOR_HINT, step: 0.05 },
  { key: 'storageFactor2d', label: 'Storage factor: 2D drawing', hint: FACTOR_HINT, step: 0.05 },
  { key: 'storageFactor3d', label: 'Storage factor: 3D model', hint: FACTOR_HINT, step: 0.05 },
  { key: 'storageFactorOrthomosaic', label: 'Storage factor: orthomosaic', hint: FACTOR_HINT, step: 0.05 },
  { key: 'storageFactorVideo360', label: 'Storage factor: 360 video', hint: FACTOR_HINT, step: 0.05 },
  { key: 'intermediateKeepDays', label: 'LAZ / harmonize kept, days', hint: 'how long the S3 lifecycle rule keeps the laz/ and harmonize/ intermediates; keep it equal to the rule', step: 1 },
  { key: 'cloudfrontUsdPerGb', label: 'CloudFront $/GB', hint: 'pay-as-you-go only: bytes past the free 1 TB a month', step: 0.001 },
  { key: 'cloudfrontUsdPer10kRequests', label: 'CloudFront $/10k requests', hint: 'pay-as-you-go only: requests past the free 10M a month', step: 0.001 },
  { key: 'egressUsdPerGb', label: 'S3 egress $/GB', hint: 'bytes leaving S3 or the app server to a browser without CloudFront: older or > 50 GB downloads, and everything on QA', step: 0.001 },
  { key: 'viewerGbPerView', label: 'Point cloud GB per session', hint: 'for sessions that sent no report, until 5 have reported; and the calculator', step: 0.05 },
  { key: 'viewerRequestsPerView', label: 'Point cloud requests per session', hint: 'for sessions that sent no report, until 5 have reported; and the calculator', step: 100 },
  { key: 'splatGbPerView', label: 'Splat GB per open', hint: CALC_VIEW_HINT, step: 0.05 },
  { key: 'splatRequestsPerView', label: 'Splat requests per open', hint: CALC_VIEW_HINT, step: 10 },
  { key: 'video360GbPerView', label: '360 video GB per open', hint: CALC_VIEW_HINT, step: 0.05 },
  { key: 'video360RequestsPerView', label: '360 video requests per open', hint: CALC_VIEW_HINT, step: 10 },
  { key: 'drawingGbPerView', label: 'Drawing GB per open', hint: CALC_VIEW_HINT, step: 0.001 },
  { key: 'drawingRequestsPerView', label: 'Drawing requests per open', hint: `${CALC_VIEW_HINT}: a tab and its ID points`, step: 1 },
  { key: 'modelGbPerView', label: '3D model GB per open', hint: CALC_VIEW_HINT, step: 0.01 },
  { key: 'modelRequestsPerView', label: '3D model requests per open', hint: `${CALC_VIEW_HINT}: one GLB`, step: 1 },
  { key: 'orthoGbPerView', label: 'Orthomosaic GB per open', hint: CALC_VIEW_HINT, step: 0.01 },
  { key: 'orthoRequestsPerView', label: 'Orthomosaic requests per open', hint: `${CALC_VIEW_HINT}: the COG tiles one look reads`, step: 10 },
  { key: 'mapTilesEsriUsdPer1000', label: 'Esri tiles $/1,000', hint: 'base map tiles past the free ones', step: 0.01 },
  { key: 'mapTilesEsriFreePerMonth', label: 'Esri free tiles/month', hint: 'ArcGIS Location Platform free tier', step: 100000 },
  { key: 'mapTilesGoogleUsdPer1000', label: 'Google tiles $/1,000', hint: 'Map Tiles API 2D tiles past the free ones', step: 0.01 },
  { key: 'mapTilesGoogleFreePerMonth', label: 'Google free tiles/month', hint: 'Map Tiles API free usage cap', step: 10000 },
  { key: 'fixedMonthlyUsd', label: 'Fixed $/month', hint: 'app servers, EBS and anything always-on', step: 1 },
];

const RANGES = [7, 30, 90];

const usd = (n: number) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd4 = (n: number) => `$${(n ?? 0).toFixed(4)}`;
const num = (n: number, dp = 1) => (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: dp });
const pct = (share: number) => (share > 0 && share < 0.005 ? '<1%' : `${Math.round((share ?? 0) * 100)}%`);

// ── Drill-down charts ─────────────────────────────────────────────────────────
// Hand-rolled SVG, light-mode admin theme. Series colors are validated
// categorical slots (see AdminCostAnalysis.module.css --viz-* tokens); the
// data table under the charts is the no-hover path to every plotted value.

interface DayRow {
  date: string; jobs: number; computeUsd: number; gbProcessed: number;
  transferUsd: number; storageGb: number; storageUsd: number; totalUsd: number;
}
interface ChartBucket extends DayRow { label: string }
type BucketUnit = 'day' | 'week' | 'month';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const bucketLabel = (key: string, unit: BucketUnit) => {
  if (unit === 'month') {
    const [y, m] = key.split('-');
    return `${MONTHS[Number(m) - 1]} ${y}`;
  }
  const d = new Date(`${key}T00:00:00Z`);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

// Days for short ranges, weeks then months as the interval grows. Flows
// (costs, GB processed, jobs) sum across the bucket; storage is a LEVEL, so a
// bucket carries its last day's figure.
const bucketize = (daily: DayRow[]): { unit: BucketUnit; buckets: ChartBucket[] } => {
  const unit: BucketUnit = daily.length <= 64 ? 'day' : daily.length <= 270 ? 'week' : 'month';
  if (unit === 'day') {
    return { unit, buckets: daily.map((d) => ({ ...d, label: bucketLabel(d.date, 'day') })) };
  }
  const keyOf = (date: string) => {
    if (unit === 'month') return date.slice(0, 7);
    const d = new Date(`${date}T00:00:00Z`);
    return new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * 86400000).toISOString().slice(0, 10);
  };
  const map = new Map<string, ChartBucket>();
  for (const d of daily) {
    const k = keyOf(d.date);
    const b = map.get(k) ?? {
      date: k, label: bucketLabel(k, unit),
      jobs: 0, computeUsd: 0, gbProcessed: 0, transferUsd: 0, storageGb: 0, storageUsd: 0, totalUsd: 0,
    };
    b.jobs += d.jobs; b.computeUsd += d.computeUsd; b.gbProcessed += d.gbProcessed;
    b.transferUsd += d.transferUsd; b.storageUsd += d.storageUsd; b.totalUsd += d.totalUsd;
    b.storageGb = d.storageGb;
    map.set(k, b);
  }
  return { unit, buckets: [...map.values()] };
};

const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
};

// Full-width charts use the wide viewBox; grid cards use the compact one so
// tick text keeps a readable rendered size after CSS scaling.
const VB_H = 220, M_L = 48, M_R = 10, M_T = 10, M_B = 24;
const PLOT_H = VB_H - M_T - M_B;

const topRoundedPath = (x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
};

interface BarSeries { key: keyof DayRow; label: string; color: string }

// Stacked (or single-series) columns with a per-column tooltip listing every
// series at that X. 4px rounded top cap on the topmost segment only, 2px
// surface gaps between segments, ≤24px bars.
const ColumnsChart = ({ buckets, series, fmt, vbWidth = 720 }: {
  buckets: ChartBucket[]; series: BarSeries[]; fmt: (v: number) => string; vbWidth?: number;
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const VB_W = vbWidth;
  const PLOT_W = VB_W - M_L - M_R;
  const totals = buckets.map((b) => series.reduce((a, s) => a + Number(b[s.key] || 0), 0));
  const ymax = niceMax(Math.max(...totals, 0));
  const band = PLOT_W / Math.max(1, buckets.length);
  const bw = Math.min(24, band * 0.72);
  const labelStep = Math.ceil(buckets.length / (VB_W < 500 ? 5 : 8));
  const yOf = (v: number) => M_T + PLOT_H - (v / ymax) * PLOT_H;
  return (
    <div className={styles.vizWrap} onPointerLeave={() => setHover(null)}>
      <svg className={styles.vizSvg} viewBox={`0 0 ${VB_W} ${VB_H}`} role="img">
        {[0.5, 1].map((f) => (
          <line key={f} x1={M_L} x2={VB_W - M_R} y1={yOf(ymax * f)} y2={yOf(ymax * f)} className={styles.vizGrid} />
        ))}
        <line x1={M_L} x2={VB_W - M_R} y1={yOf(0)} y2={yOf(0)} className={styles.vizAxis} />
        {[0, 0.5, 1].map((f) => (
          <text key={f} x={M_L - 6} y={yOf(ymax * f) + 3.5} className={styles.vizTick} textAnchor="end">
            {fmt(ymax * f)}
          </text>
        ))}
        {buckets.map((b, i) => {
          const x = M_L + i * band + (band - bw) / 2;
          let yCursor = yOf(0);
          const segs = series
            .map((s) => ({ s, v: Number(b[s.key] || 0) }))
            .filter(({ v }) => v > 0);
          const dimmed = hover !== null && hover !== i;
          return (
            <g key={b.date} opacity={dimmed ? 0.55 : 1}>
              {segs.map(({ s, v }, si) => {
                const h = Math.max(1, (v / ymax) * PLOT_H - (si > 0 ? 2 : 0));
                const y = yCursor - h - (si > 0 ? 2 : 0);
                yCursor = y;
                const isTop = si === segs.length - 1;
                return isTop
                  ? <path key={s.key} d={topRoundedPath(x, y, bw, h, 4)} fill={s.color} />
                  : <rect key={s.key} x={x} y={y} width={bw} height={h} fill={s.color} />;
              })}
              {i % labelStep === 0 && (
                <text x={M_L + i * band + band / 2} y={VB_H - 8} className={styles.vizTick} textAnchor="middle">
                  {b.label}
                </text>
              )}
              <rect
                x={M_L + i * band} y={M_T} width={band} height={PLOT_H + 6}
                fill="transparent"
                onPointerEnter={() => setHover(i)}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && buckets[hover] && (
        <div
          className={styles.vizTooltip}
          style={{ left: `${Math.min(82, Math.max(4, ((M_L + hover * band + band / 2) / VB_W) * 100))}%` }}
        >
          <p className={styles.vizTooltipTitle}>{buckets[hover].label}</p>
          {series.map((s) => (
            <p key={s.key} className={styles.vizTooltipRow}>
              <span className={styles.vizKey} style={{ background: s.color }} />
              <strong>{fmt(Number(buckets[hover][s.key] || 0))}</strong>&nbsp;{s.label}
            </p>
          ))}
          {series.length > 1 && (
            <p className={styles.vizTooltipRow}><strong>{fmt(totals[hover])}</strong>&nbsp;total</p>
          )}
        </div>
      )}
    </div>
  );
};

// Single-series trend: 2px line, 10% area wash, ≥8px end marker with a 2px
// surface ring, direct end label, crosshair tooltip snapping to the nearest X.
const TrendChart = ({ buckets, values, color, fmt, vbWidth = 720 }: {
  buckets: ChartBucket[]; values: number[]; color: string; fmt: (v: number) => string; vbWidth?: number;
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const VB_W = vbWidth;
  const PLOT_W = VB_W - M_L - M_R;
  const ymax = niceMax(Math.max(...values, 0));
  const n = values.length;
  const xOf = (i: number) => (n === 1 ? M_L + PLOT_W / 2 : M_L + (i / (n - 1)) * PLOT_W);
  const yOf = (v: number) => M_T + PLOT_H - (v / ymax) * PLOT_H;
  const linePts = values.map((v, i) => `${xOf(i)},${yOf(v)}`).join(' ');
  const areaPts = `${M_L},${yOf(0)} ${linePts} ${xOf(n - 1)},${yOf(0)}`;
  const labelStep = Math.ceil(n / (VB_W < 500 ? 4 : 6));
  const last = n - 1;
  return (
    <div className={styles.vizWrap} onPointerLeave={() => setHover(null)}>
      <svg
        className={styles.vizSvg}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        role="img"
        onPointerMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const vx = ((e.clientX - rect.left) / rect.width) * VB_W;
          const i = Math.round(((vx - M_L) / PLOT_W) * (n - 1));
          setHover(Math.max(0, Math.min(n - 1, i)));
        }}
      >
        {[0.5, 1].map((f) => (
          <line key={f} x1={M_L} x2={VB_W - M_R} y1={yOf(ymax * f)} y2={yOf(ymax * f)} className={styles.vizGrid} />
        ))}
        <line x1={M_L} x2={VB_W - M_R} y1={yOf(0)} y2={yOf(0)} className={styles.vizAxis} />
        {[0, 0.5, 1].map((f) => (
          <text key={f} x={M_L - 6} y={yOf(ymax * f) + 3.5} className={styles.vizTick} textAnchor="end">
            {fmt(ymax * f)}
          </text>
        ))}
        {buckets.map((b, i) => (i % labelStep === 0 ? (
          <text key={b.date} x={xOf(i)} y={VB_H - 8} className={styles.vizTick} textAnchor="middle">{b.label}</text>
        ) : null))}
        <polygon points={areaPts} fill={color} opacity={0.1} />
        <polyline points={linePts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <line x1={xOf(hover)} x2={xOf(hover)} y1={M_T} y2={M_T + PLOT_H} className={styles.vizCrosshair} />
        )}
        {hover !== null && (
          <circle cx={xOf(hover)} cy={yOf(values[hover])} r={4.5} fill={color} stroke="#fff" strokeWidth={2} />
        )}
        <circle cx={xOf(last)} cy={yOf(values[last])} r={4.5} fill={color} stroke="#fff" strokeWidth={2} />
        <text
          x={Math.min(xOf(last) + 8, VB_W - 4)}
          y={Math.max(yOf(values[last]) - 8, M_T + 10)}
          className={styles.vizEndLabel}
          textAnchor={xOf(last) > VB_W - 70 ? 'end' : 'start'}
        >
          {fmt(values[last])}
        </text>
      </svg>
      {hover !== null && buckets[hover] && (
        <div
          className={styles.vizTooltip}
          style={{ left: `${Math.min(82, Math.max(4, (xOf(hover) / VB_W) * 100))}%` }}
        >
          <p className={styles.vizTooltipTitle}>{buckets[hover].label}</p>
          <p className={styles.vizTooltipRow}>
            <span className={styles.vizKeyLine} style={{ background: color }} />
            <strong>{fmt(values[hover])}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

const AdminCostAnalysis = () => {
  const [rangeDays, setRangeDays] = useState(30);
  // Custom interval (both dates set = active). Presets clear it.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const customActive = !!customStart && !!customEnd;
  const today = new Date().toISOString().slice(0, 10);
  const rangeVars = {
    rangeDays,
    startDate: customActive ? customStart : null,
    endDate: customActive ? customEnd : null,
  };
  const { data, loading, error } = useQuery(ADMIN_COST_ANALYSIS_QUERY, {
    variables: rangeVars,
    fetchPolicy: 'cache-and-network',
  });

  // The table pages one ranking the server builds with the analysis and keeps
  // for a minute — asked for once the analysis is in, so it is not built twice.
  const [usersPage, setUsersPage] = usePageParam('usersPage');
  const [usersSearchInput, setUsersSearchInput] = useState('');
  const [usersSearch, setUsersSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      const next = usersSearchInput.trim();
      if (next !== usersSearch) { setUsersSearch(next); setUsersPage(1); }
    }, 300);
    return () => clearTimeout(t);
  }, [usersSearchInput]);
  useOnChange(() => setUsersPage(1), [rangeDays, customStart, customEnd]);
  const { data: usersData, previousData: previousUsersData, loading: usersLoading, error: usersError } = useQuery(ADMIN_COST_USERS_QUERY, {
    variables: { ...rangeVars, page: usersPage, limit: USERS_PAGE, search: usersSearch || undefined },
    skip: loading || !data,
    fetchPolicy: 'network-only',
  });
  const usersResult = (usersData ?? previousUsersData)?.adminCostUsers as { users: UserCost[]; total: number } | undefined;
  const costUsers = usersResult?.users ?? [];

  // Per-user drill-down: pick a row to see that user's full breakdown.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const { data: detailData, loading: detailLoading, error: detailError } = useQuery(ADMIN_USER_COST_DETAIL_QUERY, {
    variables: { userId: selectedUserId, ...rangeVars },
    skip: !selectedUserId,
    fetchPolicy: 'cache-and-network',
  });

  // Selecting a user renders the breakdown further down the page — bring it
  // into view instead of leaving the click apparently doing nothing.
  const detailRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedUserId) {
      requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [selectedUserId]);

  const analysis = data?.adminCostAnalysis;
  const detail = detailData?.adminUserCostDetail;

  const [rates, setRates] = useState<Record<RateKey, number> | null>(null);
  const [cdnPlan, setCdnPlan] = useState('free');
  // The day the plan started and the one before it: a range reaching back past
  // that day bills the earlier plan for those days.
  const [planSince, setPlanSince] = useState('');
  const [previousPlan, setPreviousPlan] = useState('free');
  const [ratesSaved, setRatesSaved] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);
  useEffect(() => {
    if (analysis?.rates) {
      setRates(Object.fromEntries(
        RATE_FIELDS.map((f) => [f.key, Number(analysis.rates[f.key]) || 0]),
      ) as Record<RateKey, number>);
      setCdnPlan(analysis.rates.cdnPlan);
      setPlanSince(analysis.rates.cdnPlanSince ?? '');
      setPreviousPlan(analysis.rates.cdnPreviousPlan ?? 'free');
    }
  }, [analysis]);
  const changePlan = (next: string) => {
    setCdnPlan(next);
    const saved = analysis?.rates?.cdnPlan;
    if (saved && next !== saved) {
      setPreviousPlan(saved);
      setPlanSince(new Date().toISOString().slice(0, 10));
    }
  };

  const [updateRates, { loading: savingRates }] = useMutation(ADMIN_UPDATE_COST_RATES_MUTATION, {
    refetchQueries: ['AdminCostAnalysis', 'AdminCostUsers'],
    onCompleted: () => {
      setRatesError(null);
      setRatesSaved(true);
      setTimeout(() => setRatesSaved(false), 2500);
    },
    onError: (e) => setRatesError(e.message),
  });

  // Puts a factor measured by the bucket check into the form; Save prices keeps it.
  const useMeasuredFactor = (key: StorageFactorKey, value: number) => {
    setRates((prev) => (prev ? { ...prev, [key]: value } : prev));
    requestAnimationFrame(() => {
      const input = document.getElementById(`rate-${key}`) as HTMLInputElement | null;
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus({ preventScroll: true });
    });
  };

  const totals = analysis?.totals;
  const averages = analysis?.averages;
  const measurement = analysis?.measurement as Measurement | undefined;
  const cdn = analysis?.cdn;
  const cdnOptions = (cdn?.options ?? []) as CdnPlanOption[];
  const cdnCurrent = cdnOptions.find((o) => o.id === cdn?.plan);
  const cdnStatus = cdnCurrent ? planStatus(cdnCurrent) : 'none';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Cost Analysis</h2>
            <p className="text-on-surface-variant mt-1">
              What the platform costs to run, and which users and file types drive it.
            </p>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.rangeTabs}>
              {RANGES.map((d) => (
                <button
                  key={d}
                  className={`${styles.rangeTab} ${!customActive && rangeDays === d ? styles.rangeTabActive : ''}`}
                  onClick={() => { setRangeDays(d); setCustomStart(''); setCustomEnd(''); }}
                >
                  {d} days
                </button>
              ))}
            </div>
            <div className={`${styles.dateRange} ${customActive ? styles.dateRangeActive : ''}`}>
              <input
                className={styles.dateInput}
                type="date"
                value={customStart}
                max={customEnd || today}
                onChange={(e) => setCustomStart(e.target.value)}
                title="Interval start"
              />
              <span className={styles.dateSep}>–</span>
              <input
                className={styles.dateInput}
                type="date"
                value={customEnd}
                min={customStart || undefined}
                max={today}
                onChange={(e) => setCustomEnd(e.target.value)}
                title="Interval end"
              />
              {(customStart || customEnd) && (
                <button
                  className={styles.dateClear}
                  title="Clear custom interval"
                  onClick={() => { setCustomStart(''); setCustomEnd(''); }}
                >
                  <Icon name="close" style={{ fontSize: 14 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        {error && !loading && (
          <p className={styles.error} role="alert">
            <Icon name="error" className="text-base" />
            The costs could not be calculated: {error.message}
          </p>
        )}

        {loading && !analysis ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Calculating costs…
          </div>
        ) : !analysis ? (
          error ? null : <p className="text-on-surface-variant">No cost data available.</p>
        ) : (
          <>
            {analysis.delivery && analysis.archive && (
              <DeliveryOverview
                delivery={analysis.delivery}
                archive={analysis.archive}
                rates={analysis.rates}
                cdnPlanLabel={cdn.planLabel}
                cdnRecommendedLabel={cdn.recommendedLabel}
              />
            )}

            {/* ── Platform totals ── */}
            <div className={styles.statGrid}>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Total</p>
                <p className={styles.statValue}>{usd(totals.totalUsd)}</p>
                <p className={styles.statHint}>
                  last {analysis.rangeDays} days
                  {totals.cdnCreditUsd > 0 && ` · after a ${usd(totals.cdnCreditUsd)} S3 credit from the CloudFront plan`}
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Conversion compute</p>
                <p className={styles.statValue}>{usd(totals.computeUsd)}</p>
                <p className={styles.statHint}>
                  {num(totals.jobs, 0)} jobs · {num(totals.runHours)} h run
                  {totals.measuredJobs > 0 && ` · ${num(totals.machineHours)} machine-h for ${num(totals.measuredJobs, 0)}`}
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Storage</p>
                <p className={styles.statValue}>{usd(totals.storageUsd)}</p>
                <p className={styles.statHint}>
                  {num(totals.storageGb)} GB in S3
                  {totals.storageCoverage < 0.995 && ` · ${pct(totals.storageCoverage)} from records, the rest estimated`}
                  {totals.archivedGb > 0 && ` · ${num(totals.archivedGb)} GB archived, saves ${usd(totals.archiveSavedUsd)}`}
                  {totals.tieringEnabled && (totals.infrequentGb + totals.instantGb) > 0
                    && ` · ${num(totals.infrequentGb + totals.instantGb)} GB in cheaper Intelligent-Tiering tiers, saves ${usd(totals.tieringSavedUsd)}`}
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Data transfer</p>
                <p className={styles.statValue}>{usd(totals.transferUsd)}</p>
                <p className={styles.statHint}>
                  {num(totals.downloads, 0)} downloads · {num(totals.downloadGb)} GB out ·
                  {totals.cdnDownloads > 0 && ` ${num(totals.cdnDownloads, 0)} through CloudFront ·`}
                  {' '}viewing {usd(totals.viewUsd + totals.viewS3Usd + totals.viewProxyUsd + totals.mapTilesUsd)}
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Fixed / always-on</p>
                <p className={styles.statValue}>{usd(totals.fixedUsd)}</p>
                <p className={styles.statHint}>app servers, EBS</p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Active users</p>
                <p className={styles.statValue}>{num(totals.activeUsers, 0)}</p>
                <p className={styles.statHint}>ran at least one job · {num(totals.billedUsers, 0)} with any cost</p>
              </div>
            </div>

            <CostOverview totals={totals as OverviewTotals} cdnPlan={cdn.plan} rangeDays={analysis.rangeDays} />

            {measurement && (
              <MeasurementPanel m={measurement} />
            )}

            {/* ── Data transfer ── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Data transfer</h3>
              <p className={styles.note}>
                Downloading a file moves all of it, which from S3 often costs more than converting it
                did. On prod every viewer file (point clouds, splats, 360 videos, panoramas,
                orthomosaics, drawings and 3D models) comes from CloudFront; without a distribution
                (QA) they come from S3 or through the app server. PDFs open in the browser&apos;s own
                reader straight from S3, the whole file each time. The Scene&apos;s base map comes from
                the map provider. The viewers report what they fetched, and sessions that
                did not report are estimated. CloudFront costs what its plan costs (see below), and
                each user carries the share of the allowance they used. Traffic through a share link
                is billed to whoever made the link.
              </p>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Traffic</th><th>Events</th><th>Of which shared</th>
                      <th>GB out</th><th>Rate</th><th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.strong}>
                        File downloads from S3
                        {totals.cdnDownloads > 0 && <span className={styles.sub}>over 50 GB, archived or uploaded before names were stored</span>}
                      </td>
                      <td>{num(totals.downloads - totals.cdnDownloads, 0)}</td>
                      <td>{num(totals.shareDownloads, 0)}{totals.shareDownloadGb > 0 && ` · ${num(totals.shareDownloadGb)} GB`}</td>
                      <td>{num(totals.downloadGb - totals.cdnDownloadGb)} GB</td>
                      <td>{usd4(analysis.rates.egressUsdPerGb)}/GB</td>
                      <td className={styles.strong}>{usd(totals.downloadUsd - totals.cdnDownloadUsd)}</td>
                    </tr>
                    {totals.cdnDownloads > 0 && (
                      <tr>
                        <td className={styles.strong}>
                          File downloads through CloudFront
                          <span className={styles.sub}>signed links, part of the plan</span>
                        </td>
                        <td>{num(totals.cdnDownloads, 0)}</td>
                        <td>—</td>
                        <td>{num(totals.cdnDownloadGb)} GB</td>
                        <td>{cdn.plan === 'payg' ? `${usd4(analysis.rates.cloudfrontUsdPerGb)}/GB` : `${cdn.planLabel} plan, flat`}</td>
                        <td className={styles.strong}>{usd(totals.cdnDownloadUsd)}</td>
                      </tr>
                    )}
                    {totals.docViews > 0 && (
                      <tr>
                        <td className={styles.strong}>
                          PDF and document viewing
                          <span className={styles.sub}>the browser&apos;s reader loads the whole file from S3</span>
                        </td>
                        <td>{num(totals.docViews, 0)}</td>
                        <td>—</td>
                        <td>{num(totals.docViewGb, 2)} GB</td>
                        <td>{usd4(analysis.rates.egressUsdPerGb)}/GB</td>
                        <td className={styles.strong}>{usd(totals.docViewUsd)}</td>
                      </tr>
                    )}
                    <tr>
                      <td className={styles.strong}>
                        Viewer streaming (CloudFront)
                        <span className={styles.sub}>{num(totals.reportedViews, 0)} of {num(totals.views, 0)} sessions measured</span>
                      </td>
                      <td>{num(totals.views, 0)}</td>
                      <td>{num(totals.shareViews, 0)}</td>
                      <td>
                        {num(totals.viewGb)} GB
                        <span className={styles.sub}>{fmtCount(totals.viewRequests)} requests</span>
                      </td>
                      <td>
                        {cdn.plan === 'payg'
                          ? `${usd4(analysis.rates.cloudfrontUsdPerGb)}/GB + ${usd4(analysis.rates.cloudfrontUsdPer10kRequests)}/10k req`
                          : `${cdn.planLabel} plan, flat`}
                      </td>
                      <td className={styles.strong}>{usd(totals.viewUsd)}</td>
                    </tr>
                    <tr>
                      <td className={styles.strong}>
                        Viewer data from S3
                        <span className={styles.sub}>where no CloudFront serves it (QA), and thumbnails</span>
                      </td>
                      <td>—</td>
                      <td>—</td>
                      <td>{num(totals.viewS3Gb, 2)} GB</td>
                      <td>{usd4(analysis.rates.egressUsdPerGb)}/GB</td>
                      <td className={styles.strong}>{usd(totals.viewS3Usd)}</td>
                    </tr>
                    {totals.viewProxyGb > 0 && (
                      <tr>
                        <td className={styles.strong}>
                          Viewer data through the app server
                          <span className={styles.sub}>octree and panorama proxy (no CloudFront)</span>
                        </td>
                        <td>—</td>
                        <td>—</td>
                        <td>{num(totals.viewProxyGb, 2)} GB</td>
                        <td>{usd4(analysis.rates.egressUsdPerGb)}/GB</td>
                        <td className={styles.strong}>{usd(totals.viewProxyUsd)}</td>
                      </tr>
                    )}
                    {(totals.mapTilesEsri + totals.mapTilesGoogle + totals.mapTilesOsm) > 0 && (
                      <tr>
                        <td className={styles.strong}>
                          Base map tiles
                          <span className={styles.sub}>
                            {[
                              totals.mapTilesEsri > 0 && `Esri ${fmtCount(totals.mapTilesEsri)}`,
                              totals.mapTilesGoogle > 0 && `Google ${fmtCount(totals.mapTilesGoogle)}`,
                              totals.mapTilesOsm > 0 && `OpenStreetMap ${fmtCount(totals.mapTilesOsm)}`,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        </td>
                        <td>{fmtCount(totals.mapTilesEsri + totals.mapTilesGoogle + totals.mapTilesOsm)}</td>
                        <td>—</td>
                        <td>—</td>
                        <td>past the free tiles a month</td>
                        <td className={styles.strong}>{usd(totals.mapTilesUsd)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className={styles.note}>
                Average cost per download {usd4(averages.perDownloadUsd)}.
              </p>
            </div>

            {/* ── CloudFront plan ── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>CloudFront plan</h3>
              <p className={styles.note}>
                Every viewer file and the downloads up to 50 GB come from CloudFront. A flat-rate plan costs the
                same whatever the traffic and never bills overage, but AWS slows delivery when months
                keep going past the allowance, so the question is which plan the traffic fits.
                {' '}{num(cdn.reportedSessions, 0)} sessions reported their traffic
                ({fmtCount(cdn.measuredRequests)} requests, {fmtGb(cdn.measuredGb)}).
                {cdn.estimatedSessions > 0 && ` ${num(cdn.estimatedSessions, 0)} did not report and count as ${num(cdn.perSessionRequests, 0)} requests and ${num(cdn.perSessionGb, 2)} GB each (${cdn.perSessionSource === 'measured' ? 'the reported average' : 'Unit prices'}).`}
                {' '}The traffic is scaled to a month. The console has AWS&apos;s own count: CloudFront →
                the distribution → Manage plan.
              </p>
              <div className={`${styles.statGrid} ${styles.cdnStats}`}>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Plan in use</p>
                  <p className={styles.statValue}>{cdn.planLabel}</p>
                  <p className={styles.statHint}>
                    {usd(cdn.usd)} for these {analysis.rangeDays} days
                    {cdn.previousPlanDays > 0 && ` · since ${cdn.planSince}, ${cdn.previousPlanLabel} for the ${num(cdn.previousPlanDays, 0)} days before`}
                  </p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Traffic a month</p>
                  <p className={styles.statValue}>{fmtCount(cdn.monthlyRequests)} req</p>
                  <p className={styles.statHint}>
                    {fmtGb(cdn.monthlyGb)} · from {num(cdn.views, 0)} sessions in the range
                    {cdn.downloads > 0 && ` and ${num(cdn.downloads, 0)} downloads (${fmtGb(cdn.downloadGb)})`}
                  </p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Allowance used</p>
                  <p className={styles.statValue}>{cdn.plan === 'payg' ? '—' : pct(planShare(cdn))}</p>
                  <p className={styles.statHint}>
                    {cdn.plan === 'payg'
                      ? 'pay-as-you-go has no allowance, only a free tier'
                      : `requests ${pct(cdn.requestShare)} · data ${pct(cdn.gbShare)}`}
                  </p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Recommended</p>
                  <p className={styles.statValue}>{cdn.recommendedLabel}</p>
                  <p className={styles.statHint}>cheapest plan this traffic fits with 20% to spare</p>
                </div>
              </div>
              {cdnStatus === 'over' && (
                <p className={`${styles.warn} ${styles.spaced}`}>
                  The traffic is past the {cdn.planLabel} allowance. Check the console (CloudFront →
                  the distribution → Manage plan): if it agrees, move to {cdn.recommendedLabel} there
                  (Change plan) and pick it here. AWS tolerates one month of up to 3×; several months
                  in a row over it get slower delivery.
                  {cdn.estimatedSessions > cdn.reportedSessions && ' Most sessions here are estimates, so the console has the final word.'}
                </p>
              )}
              {cdnStatus === 'near' && (
                <p className={`${styles.warn} ${styles.spaced}`}>
                  The traffic is above 80% of the {cdn.planLabel} allowance. AWS emails at 80% and 100%
                  of its own count. Plan the move to {cdn.recommendedLabel} if the console agrees.
                </p>
              )}
              <CdnPlanTable options={cdnOptions} current={cdn.plan} recommended={cdn.recommended} />
              <p className={`${styles.note} ${styles.cdnFoot}`}>
                The plans include an S3 credit for Standard storage anywhere in the account
                {cdn.s3CreditUsd > 0 && ` (${usd(cdn.s3CreditUsd)} in this range, taken off the total)`}.
              </p>
            </div>

            {measurement && <StorageOverview s={measurement.storage} storageUsd={totals.storageUsd} />}

            <BucketCheck
              factorControl={rates && analysis?.rates ? {
                saved: Object.fromEntries(Object.values(FACTOR_RATE_KEY).map((k) => [k, Number(analysis.rates[k]) || 0])),
                inForm: rates,
                onUse: useMeasuredFactor,
              } : undefined}
            />

            {/* ── By content type and by lane ── */}
            <div className={styles.twoCol}>
              {[{ title: 'Cost by file type', rows: analysis.byCategory as Bucket[] },
                { title: 'Cost by compute lane', rows: analysis.byLane as Bucket[] }].map((table) => (
                <div key={table.title} className={styles.panel}>
                  <h3 className={styles.panelTitle}>{table.title}</h3>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Type</th><th>Jobs</th><th>Run h</th><th title="Machine time measured for these jobs">Machine h</th>
                        <th>Cost</th><th>Per job</th><th>Per GB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.length === 0 && (
                        <tr><td colSpan={7} className={styles.empty}>No jobs in this period</td></tr>
                      )}
                      {table.rows.map((b) => (
                        <tr key={b.key}>
                          <td className={styles.strong}>{b.label}</td>
                          <td>{num(b.jobs, 0)}</td>
                          <td>{num(b.runHours)}</td>
                          <td>{b.machineHours > 0 ? num(b.machineHours) : '—'}</td>
                          <td className={styles.strong}>{usd(b.computeUsd)}</td>
                          <td>{usd4(b.avgPerJobUsd)}</td>
                          <td>{usd4(b.avgPerGbUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            {/* ── Per user ── */}
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h3 className={styles.panelTitle}>Cost per user</h3>
                  <p className={styles.note}>Select a user for their full breakdown.</p>
                </div>
                <input
                  type="search"
                  className={styles.userSearch}
                  placeholder="Search by name, email or company"
                  aria-label="Search users"
                  value={usersSearchInput}
                  onChange={(e) => setUsersSearchInput(e.target.value)}
                />
              </div>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>User</th><th>Jobs</th><th>Hours</th><th>Compute</th>
                      <th>Stored</th><th>Storage</th><th>Downloads</th><th>Transfer</th>
                      <th>Total</th><th>Per job</th><th>Per GB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersError && costUsers.length === 0 && (
                      <tr><td colSpan={11} className={styles.empty} role="alert">The users could not be loaded: {usersError.message}</td></tr>
                    )}
                    {!usersResult && !usersError && (
                      <tr><td colSpan={11} className={styles.empty}>Loading users…</td></tr>
                    )}
                    {usersResult && !usersLoading && costUsers.length === 0 && (
                      <tr>
                        <td colSpan={11} className={styles.empty}>
                          {usersSearch ? `No user matches “${usersSearch}”` : 'No activity in this period'}
                        </td>
                      </tr>
                    )}
                    {costUsers.map((u) => (
                      <tr
                        key={u.userId}
                        className={`${styles.clickable} ${selectedUserId === u.userId ? styles.rowActive : ''}`}
                        onClick={() => {
                          setSelectedName(u.name);
                          setSelectedUserId((prev) => (prev === u.userId ? null : u.userId));
                        }}
                        title="Show this user's breakdown"
                      >
                        <td>
                          <span className={styles.strong}>{u.name}</span>
                          <span className={styles.sub}>{u.company || u.email}</span>
                          {u.subusers > 0 && (
                            <span className={styles.sub} title="Their subusers' costs are counted here">
                              + {u.subusers} subuser{u.subusers === 1 ? '' : 's'}
                            </span>
                          )}
                        </td>
                        <td>{num(u.jobs, 0)}{u.failedJobs > 0 && <span className={styles.failed}> · {u.failedJobs} failed</span>}</td>
                        <td>{num(u.runHours, 2)}</td>
                        <td>{usd(u.computeUsd)}</td>
                        <td>{num(u.storageGb)} GB</td>
                        <td>{usd(u.storageUsd)}</td>
                        <td>
                          {num(u.downloads, 0)}
                          {u.shareDownloads > 0 && <span className={styles.sub}>{num(u.shareDownloads, 0)} shared</span>}
                        </td>
                        <td>{usd(u.transferUsd)}</td>
                        <td className={styles.strong}>{usd(u.totalUsd)}</td>
                        <td>{usd4(u.avgPerConversionUsd)}</td>
                        <td>{usd4(u.avgPerGbUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={usersPage}
                pageSize={USERS_PAGE}
                total={usersResult?.total ?? 0}
                onPageChange={setUsersPage}
                noun="users"
                variant="inline"
              />
            </div>

            {/* ── One user in detail ── */}
            {selectedUserId && (
              <div className={styles.panel} ref={detailRef}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelTitle}>
                    {detail
                      ? `${detail.name} — cost breakdown`
                      : detailError ? `${selectedName ?? 'User'} — cost breakdown` : 'Loading user…'}
                  </h3>
                  <button className={styles.secondaryBtn} onClick={() => setSelectedUserId(null)}>
                    <Icon name="close" className="text-sm" />
                    Close
                  </button>
                </div>

                {detailLoading && !detail ? (
                  <div className="flex items-center py-6 text-on-surface-variant">
                    <Icon name="refresh" className="animate-spin mr-2" />
                    Calculating…
                  </div>
                ) : !detail ? (
                  detailError ? (
                    <p className={styles.error} role="alert">
                      <Icon name="error" className="text-base" />
                      This user&apos;s breakdown could not be loaded: {detailError.message}
                    </p>
                  ) : null
                ) : (
                  <>
                    <p className={styles.note}>
                      {detail.company || detail.email} · {detail.totals.jobs} jobs over {detail.rangeDays} days
                      {detail.totals.failedJobs > 0 && ` · ${detail.totals.failedJobs} failed`}
                    </p>

                    <div className={styles.statGrid}>
                      <div className={styles.statCard}>
                        <p className={styles.statLabel}>Total</p>
                        <p className={styles.statValue}>{usd(detail.totals.totalUsd)}</p>
                        <p className={styles.statHint}>
                          platform average {usd(analysis.averages.perUserUsd)} per user
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <p className={styles.statLabel}>Compute</p>
                        <p className={styles.statValue}>{usd(detail.totals.computeUsd)}</p>
                        <p className={styles.statHint}>
                          {num(detail.totals.runHours, 2)} h run
                          {detail.totals.measuredJobs > 0 && ` · ${num(detail.totals.machineHours, 2)} machine-h for ${num(detail.totals.measuredJobs, 0)} jobs`}
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <p className={styles.statLabel}>Storage</p>
                        <p className={styles.statValue}>{usd(detail.totals.storageUsd)}</p>
                        <p className={styles.statHint}>
                          {num(detail.totals.storageGb)} GB in S3
                          {detail.totals.storageCoverage > 0 ? ' · measured' : ' · estimated'}
                          {detail.totals.archivedGb > 0 && ` · ${num(detail.totals.archivedGb)} GB archived, saves ${usd(detail.totals.archiveSavedUsd)}`}
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <p className={styles.statLabel}>Data transfer</p>
                        <p className={styles.statValue}>{usd(detail.totals.transferUsd)}</p>
                        <p className={styles.statHint}>
                          {num(detail.totals.downloads, 0)} downloads ({num(detail.totals.downloadGb)} GB)
                          {detail.totals.shareDownloads > 0 && `, ${num(detail.totals.shareDownloads, 0)} via share links`}
                          {detail.totals.views > 0 && ` · ${num(detail.totals.views, 0)} viewer sessions (${num(detail.totals.reportedViews, 0)} measured)`}
                        </p>
                      </div>
                      <div className={styles.statCard}>
                        <p className={styles.statLabel}>Per conversion</p>
                        <p className={styles.statValue}>{usd4(detail.averages.perConversionUsd)}</p>
                        <p className={styles.statHint}>
                          platform {usd4(detail.platformAverages.perConversionUsd)}
                          {detail.platformAverages.perConversionUsd > 0 && (
                            ` · ${Math.round((detail.averages.perConversionUsd / detail.platformAverages.perConversionUsd) * 100)}% of average`
                          )}
                        </p>
                      </div>
                    </div>

                    <div className={styles.twoCol}>
                      {[{ title: 'By file type', rows: detail.byCategory as Bucket[] },
                        { title: 'By compute lane', rows: detail.byLane as Bucket[] }].map((t) => (
                        <div key={t.title}>
                          <h4 className={styles.subTitle}>{t.title}</h4>
                          <table className={styles.table}>
                            <thead>
                              <tr><th>Type</th><th>Jobs</th><th>Hours</th><th>Cost</th><th>Per job</th></tr>
                            </thead>
                            <tbody>
                              {t.rows.length === 0 && (
                                <tr><td colSpan={5} className={styles.empty}>No jobs in this period</td></tr>
                              )}
                              {t.rows.map((b) => (
                                <tr key={b.key}>
                                  <td className={styles.strong}>{b.label}</td>
                                  <td>{num(b.jobs, 0)}</td>
                                  <td>{num(b.runHours, 2)}</td>
                                  <td className={styles.strong}>{usd(b.computeUsd)}</td>
                                  <td>{usd4(b.avgPerJobUsd)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>

                    <h4 className={styles.subTitle}>By project</h4>
                    <div className={styles.tableScroll}>
                      <table className={styles.table}>
                        <thead>
                          <tr><th>Project</th><th>Jobs</th><th>Compute</th><th>Stored</th><th>Storage</th><th>Total</th></tr>
                        </thead>
                        <tbody>
                          {detail.byProject.length === 0 && (
                            <tr><td colSpan={6} className={styles.empty}>No projects owned by this user</td></tr>
                          )}
                          {detail.byProject.map((p: {
                            projectId: string; name: string; jobs: number;
                            computeUsd: number; storageGb: number; storageUsd: number; totalUsd: number;
                          }) => (
                            <tr key={p.projectId}>
                              <td className={styles.strong}>{p.name}</td>
                              <td>{num(p.jobs, 0)}</td>
                              <td>{usd(p.computeUsd)}</td>
                              <td>{num(p.storageGb)} GB</td>
                              <td>{usd(p.storageUsd)}</td>
                              <td className={styles.strong}>{usd(p.totalUsd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {detail.topDownloads.length > 0 && (
                      <>
                        <h4 className={styles.subTitle}>Most downloaded files</h4>
                        <div className={styles.tableScroll}>
                          <table className={styles.table}>
                            <thead>
                              <tr><th>File</th><th>Downloads</th><th>Via share link</th><th>GB out</th><th>Egress</th></tr>
                            </thead>
                            <tbody>
                              {detail.topDownloads.map((d: {
                                fileName: string; downloads: number; shareDownloads: number;
                                gb: number; egressUsd: number;
                              }, i: number) => (
                                <tr key={`${d.fileName}-${i}`}>
                                  <td className={styles.strong}>{d.fileName}</td>
                                  <td>{num(d.downloads, 0)}</td>
                                  <td>{num(d.shareDownloads, 0)}</td>
                                  <td>{num(d.gb)} GB</td>
                                  <td className={styles.strong}>{usd(d.egressUsd)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    <h4 className={styles.subTitle}>Most expensive jobs</h4>
                    <div className={styles.tableScroll}>
                      <table className={styles.table}>
                        <thead>
                          <tr><th>File</th><th>Type</th><th>Lane</th><th>Run h</th><th>Machine h</th><th>Size</th><th>Cost</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {detail.topJobs.length === 0 && (
                            <tr><td colSpan={8} className={styles.empty}>No jobs in this period</td></tr>
                          )}
                          {detail.topJobs.map((j: {
                            fileName: string; mode: string; lane: string; status: string;
                            submittedAt: string; runHours: number; machineHours: number | null; gb: number; computeUsd: number;
                          }, i: number) => (
                            <tr key={`${j.fileName}-${j.submittedAt}-${i}`}>
                              <td className={styles.strong}>{j.fileName}</td>
                              <td>{j.mode}</td>
                              <td>{j.lane}</td>
                              <td>{num(j.runHours, 2)}</td>
                              <td>{j.machineHours != null ? num(j.machineHours, 2) : '—'}</td>
                              <td>{num(j.gb)} GB</td>
                              <td className={styles.strong}>{usd4(j.computeUsd)}</td>
                              <td className={j.status === 'succeeded' ? '' : styles.failed}>{j.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {detail.optimizations?.length > 0 && (
                      <>
                        <h4 className={styles.subTitle}>Cost optimizations</h4>
                        <p className={styles.note}>
                          Estimated from this user's measured usage and the unit prices below. Tier
                          savings assume AWS list-price ratios (Standard-IA ≈ 54%, Glacier Instant
                          Retrieval ≈ 17% of S3 Standard).
                        </p>
                        <div className={styles.tipGrid}>
                          {(detail.optimizations as {
                            id: string; impact: string; icon: IconName; title: string;
                            detail: string; savingUsdMonthly: number;
                          }[]).map((t) => (
                            <div key={t.id} className={styles.tipCard} data-impact={t.impact}>
                              <div className={styles.tipHead}>
                                <Icon name={t.icon} className={styles.tipIcon} />
                                <p className={styles.tipTitle}>{t.title}</p>
                                {t.savingUsdMonthly > 0 && (
                                  <span className={styles.tipSaving}>~{usd(t.savingUsdMonthly)}/mo</span>
                                )}
                              </div>
                              <p className={styles.tipDetail}>{t.detail}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {detail.daily.length > 0 && (() => {
                      const { unit, buckets } = bucketize(detail.daily as DayRow[]);
                      const cumStorage: number[] = [];
                      let acc = 0;
                      for (const b of buckets) { acc += b.storageUsd; cumStorage.push(acc); }
                      const unitLabel = unit === 'day' ? 'per day' : unit === 'week' ? 'per week' : 'per month';
                      const gbFmt = (v: number) => `${num(v, v < 10 ? 1 : 0)} GB`;
                      const usdFmt = (v: number) => (v < 1 && v > 0 ? usd4(v) : usd(v));
                      const COST_SERIES: BarSeries[] = [
                        { key: 'computeUsd', label: 'Compute', color: 'var(--viz-compute)' },
                        { key: 'storageUsd', label: 'Storage', color: 'var(--viz-storage)' },
                        { key: 'transferUsd', label: 'Transfer', color: 'var(--viz-transfer)' },
                      ];
                      return (
                        <div className={styles.charts}>
                          <div className={styles.chartCard}>
                            <div className={styles.chartHead}>
                              <h4 className={styles.subTitle}>Cost over time <span className={styles.chartUnit}>({unitLabel})</span></h4>
                              <div className={styles.vizLegend}>
                                {COST_SERIES.map((s) => (
                                  <span key={s.key} className={styles.vizLegendItem}>
                                    <span className={styles.vizKey} style={{ background: s.color }} />
                                    {s.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <ColumnsChart buckets={buckets} series={COST_SERIES} fmt={usdFmt} />
                          </div>

                          <div className={styles.chartGrid}>
                            <div className={styles.chartCard}>
                              <h4 className={styles.subTitle}>Stored data <span className={styles.chartUnit}>(GB held)</span></h4>
                              <TrendChart
                                buckets={buckets}
                                values={buckets.map((b) => b.storageGb)}
                                color="var(--viz-storage)"
                                fmt={gbFmt}
                                vbWidth={380}
                              />
                            </div>
                            <div className={styles.chartCard}>
                              <h4 className={styles.subTitle}>Storage cost, accumulated</h4>
                              <TrendChart
                                buckets={buckets}
                                values={cumStorage}
                                color="var(--viz-storage)"
                                fmt={usdFmt}
                                vbWidth={380}
                              />
                            </div>
                            <div className={styles.chartCard}>
                              <h4 className={styles.subTitle}>Data processed <span className={styles.chartUnit}>({unitLabel})</span></h4>
                              <ColumnsChart
                                buckets={buckets}
                                series={[{ key: 'gbProcessed', label: 'Converted', color: 'var(--viz-compute)' }]}
                                fmt={gbFmt}
                                vbWidth={380}
                              />
                            </div>
                          </div>

                          <details className={styles.chartTable}>
                            <summary>Show data table</summary>
                            <div className={styles.tableScroll}>
                              <table className={styles.table}>
                                <thead>
                                  <tr>
                                    <th>Period</th><th>Jobs</th><th>Compute</th><th>Processed</th>
                                    <th>Stored</th><th>Storage</th><th>Transfer</th><th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {buckets.map((b) => (
                                    <tr key={b.date}>
                                      <td className={styles.strong}>{b.label}</td>
                                      <td>{num(b.jobs, 0)}</td>
                                      <td>{usd4(b.computeUsd)}</td>
                                      <td>{num(b.gbProcessed)} GB</td>
                                      <td>{num(b.storageGb)} GB</td>
                                      <td>{usd4(b.storageUsd)}</td>
                                      <td>{usd4(b.transferUsd)}</td>
                                      <td className={styles.strong}>{usd4(b.totalUsd)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* ── Rates ── */}
            {rates && (
              <div className={styles.panel}>
                <h3 className={styles.panelTitle}>Unit prices</h3>
                <p className={styles.note}>
                  Used for every figure on this page. Machine prices are per whole machine, its EBS
                  volume included, and apply to jobs whose machine time is measured. Lane prices are
                  per job-hour and apply to the rest, so a lane that packs two jobs per machine
                  carries half the instance price. Storage factors and the per-open viewer figures for
                  splats, videos, drawings, models and orthomosaics feed only the calculator.
                </p>
                <div className={styles.rateGrid}>
                  <label className={styles.rateField}>
                    <span className={styles.rateLabel}>CloudFront plan</span>
                    <select
                      className="input-field"
                      aria-label="CloudFront plan"
                      value={cdnPlan}
                      onChange={(e) => changePlan(e.target.value)}
                    >
                      {cdnOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.id === 'payg' ? o.label : `${o.label} — $${num(o.usdMonthly, 0)}/month`}
                        </option>
                      ))}
                    </select>
                    <span className={styles.rateHint}>the plan the cdn.infrio.cloud distribution is on</span>
                  </label>
                  <label className={styles.rateField}>
                    <span className={styles.rateLabel}>CloudFront plan since</span>
                    <input
                      className="input-field"
                      aria-label="CloudFront plan since"
                      type="date"
                      value={planSince}
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setPlanSince(e.target.value)}
                    />
                    <span className={styles.rateHint}>the day AWS switched the distribution to it; empty = always</span>
                  </label>
                  <label className={styles.rateField}>
                    <span className={styles.rateLabel}>CloudFront plan before that</span>
                    <select className="input-field" aria-label="CloudFront plan before that" value={previousPlan} onChange={(e) => setPreviousPlan(e.target.value)}>
                      {cdnOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.id === 'payg' ? o.label : `${o.label} — $${num(o.usdMonthly, 0)}/month`}
                        </option>
                      ))}
                    </select>
                    <span className={styles.rateHint}>billed for the days before that one</span>
                  </label>
                  {RATE_FIELDS.map((f) => {
                    const saved = Number(analysis?.rates?.[f.key]) || 0;
                    const unsaved = Math.abs(rates[f.key] - saved) > 1e-9;
                    return (
                      <label key={f.key} className={`${styles.rateField} ${unsaved ? styles.rateUnsaved : ''}`}>
                        <span className={styles.rateLabel}>{f.label}</span>
                        <input
                          id={`rate-${f.key}`}
                          className="input-field"
                          type="number"
                          min={0}
                          step={f.step}
                          value={rates[f.key]}
                          onChange={(e) => setRates((prev) => (prev
                            ? { ...prev, [f.key]: Math.max(0, Number(e.target.value) || 0) }
                            : prev))}
                        />
                        <span className={styles.rateHint}>{f.hint}</span>
                        {unsaved && <span className={styles.unsavedTag}>Not saved yet (saved: {num(saved, 4)})</span>}
                      </label>
                    );
                  })}
                </div>
                <div className={styles.saveRow}>
                  {ratesError && <span className={styles.saveError} role="alert">Not saved: {ratesError}</span>}
                  {ratesSaved && <span className={styles.saved}>Saved</span>}
                  <button
                    className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70"
                    onClick={() => {
                      setRatesError(null);
                      void updateRates({ variables: { input: { ...rates, cdnPlan, cdnPlanSince: planSince, cdnPreviousPlan: previousPlan } } });
                    }}
                    disabled={savingRates}
                  >
                    {savingRates ? 'Saving…' : 'Save prices'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminCostAnalysis;
